<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import { Copy } from 'lucide-vue-next'
import { DATE_INPUT_MAX_LENGTH, formatDateInput, parseDateInput } from '@ck/domain'
import { accountsApi, ApiError, customersApi, vehiclesApi, workOrdersApi, type CreateCustomerAccountResponse, type CustomerSummary } from '@ck/api-client'
import { CkAlert, CkButton, CkField } from '@ck/ui'
import BottomNav from '../components/BottomNav.vue'

const router = useRouter()
const queryClient = useQueryClient()

const customerForm = reactive({ name: '', email: '', phone: '' })
const vehicleForm = reactive({ brand: '', model: '', year: '', plate: '', vin: '', color: '' })
const expectedDelivery = ref('')

const searchTerm = ref('')
const activeSearch = ref('')
const searched = ref(false)
const selectedCustomer = ref<CustomerSummary | null>(null)
const customersQuery = useQuery(computed(() => ({
  queryKey: ['customers-search', activeSearch.value],
  queryFn: () => customersApi.list(activeSearch.value),
  enabled: activeSearch.value.trim().length >= 2
})))
const searchResults = computed(() => customersQuery.data.value?.items ?? [])

function runSearch() {
  searched.value = true
  activeSearch.value = searchTerm.value.trim()
}
function selectCustomer(customer: CustomerSummary) {
  selectedCustomer.value = customer
  errors.value.name = undefined
  errors.value.email = undefined
}
function clearSelection() {
  selectedCustomer.value = null
  createdAccount.value = null
}

const createdAccount = ref<CreateCustomerAccountResponse | null>(null)
const createdWorkOrderId = ref<string | null>(null)
const copied = ref(false)
async function copyPassword() {
  if (!createdAccount.value) return
  await navigator.clipboard.writeText(createdAccount.value.temporaryPassword)
  copied.value = true
}

interface FormErrors {
  name?: string; email?: string; brand?: string; model?: string; year?: string; plate?: string; expectedDelivery?: string
}
const errors = ref<FormErrors>({})
const submitError = ref<string | null>(null)

const mutation = useMutation({
  mutationFn: async () => {
    createdAccount.value = null
    let customerId: string
    if (selectedCustomer.value) {
      customerId = selectedCustomer.value.id
    } else {
      const account = await accountsApi.createCustomerAccount({
        name: customerForm.name.trim(),
        email: customerForm.email.trim(),
        phone: customerForm.phone.trim() || null
      })
      createdAccount.value = account
      customerId = account.customerId
    }
    const vehicle = await vehiclesApi.create({
      customerId,
      brand: vehicleForm.brand.trim(),
      model: vehicleForm.model.trim(),
      year: Number(vehicleForm.year),
      plate: vehicleForm.plate.trim().toUpperCase(),
      vin: vehicleForm.vin.trim() || null,
      color: vehicleForm.color.trim() || null
    })
    // Sem técnico responsável no formulário: o mecânico que cria a OS pelo Checklist é
    // sempre autoatribuído pela própria API (ver WorkOrderEndpoints.Create).
    return workOrdersApi.create({
      vehicleId: vehicle.id,
      expectedDelivery: parseDateInput(expectedDelivery.value)
    })
  },
  onSuccess: async (workOrder) => {
    await queryClient.invalidateQueries({ queryKey: ['my-work-orders'] })
    createdWorkOrderId.value = workOrder.id
    if (!createdAccount.value) goToWorkOrder()
  },
  onError: (error: unknown) => {
    submitError.value = error instanceof ApiError ? (error.problem.detail ?? error.problem.title) : 'Não foi possível criar a ordem de serviço.'
  }
})

function goToWorkOrder() {
  if (createdWorkOrderId.value) router.push({ name: 'veiculo', params: { id: createdWorkOrderId.value } })
  else router.push({ name: 'home' })
}

function updateExpectedDelivery(value: string) {
  expectedDelivery.value = formatDateInput(value)
  if (errors.value.expectedDelivery) errors.value = { ...errors.value, expectedDelivery: undefined }
}

function validate(): boolean {
  const next: FormErrors = {}
  if (!selectedCustomer.value) {
    if (customerForm.name.trim().length < 3) next.name = 'Nome deve ter ao menos 3 caracteres.'
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(customerForm.email.trim())) next.email = 'E-mail inválido.'
  }
  if (!vehicleForm.brand.trim()) next.brand = 'Informe a marca.'
  if (!vehicleForm.model.trim()) next.model = 'Informe o modelo.'
  const year = Number(vehicleForm.year)
  if (!vehicleForm.year || year < 1886 || year > 2100) next.year = 'Ano inválido.'
  if (!vehicleForm.plate.trim()) next.plate = 'Informe a placa.'
  if (expectedDelivery.value && !parseDateInput(expectedDelivery.value)) next.expectedDelivery = 'Informe uma data válida no formato dd/MM/yyyy.'
  errors.value = next
  return Object.keys(next).length === 0
}

function submit() {
  submitError.value = null
  if (!validate()) return
  mutation.mutate()
}
</script>

<template>
  <div class="app">
    <header><h1>Nova OS</h1></header>

    <CkAlert v-if="createdAccount" tone="success" class="card">
      OS criada. Conta de acesso ao Portal do Cliente criada para <strong>{{ createdAccount.name }}</strong>. Senha temporária — anote agora, ela não será exibida novamente:
      <div class="temp-password">
        <code class="ck-code">{{ createdAccount.temporaryPassword }}</code>
        <button type="button" class="copy" @click="copyPassword"><Copy :size="14" />{{ copied ? 'Copiada' : 'Copiar' }}</button>
      </div>
      O cliente deverá trocá-la no primeiro acesso ao Portal.
      <CkButton class="continue" @click="goToWorkOrder">Ir para a OS</CkButton>
    </CkAlert>

    <form v-else class="card" @submit.prevent="submit">
      <CkAlert v-if="submitError" tone="error">{{ submitError }}</CkAlert>

      <section class="group">
        <h2>Cliente</h2>
        <div v-if="!selectedCustomer" class="customer-search">
          <CkField v-model="searchTerm" label="Buscar cliente existente" hint="Nome ou e-mail" :disabled="mutation.isPending.value" @keyup.enter.prevent="runSearch" />
          <CkButton type="button" variant="secondary" :disabled="mutation.isPending.value" @click="runSearch">Buscar</CkButton>
          <ul v-if="searchResults.length" class="search-results">
            <li v-for="customer in searchResults" :key="customer.id">
              <button type="button" @click="selectCustomer(customer)">
                <strong>{{ customer.name }}</strong>
                <span>{{ customer.email }}</span>
              </button>
            </li>
          </ul>
          <p v-else-if="searched && activeSearch && !customersQuery.isFetching.value" class="empty-inline">
            Nenhum cliente encontrado para "{{ activeSearch }}". Preencha os dados abaixo para cadastrar um novo cliente.
          </p>
        </div>
        <div v-else class="selected-customer">
          <div><strong>{{ selectedCustomer.name }}</strong><span>{{ selectedCustomer.email }}</span></div>
          <CkButton type="button" variant="ghost" @click="clearSelection">Trocar cliente</CkButton>
        </div>
        <template v-if="!selectedCustomer">
          <CkField v-model="customerForm.name" label="Nome completo" autocomplete="name" required :error="errors.name" :disabled="mutation.isPending.value" />
          <CkField v-model="customerForm.email" label="E-mail" type="email" autocomplete="email" required :error="errors.email" :disabled="mutation.isPending.value" />
          <CkField v-model="customerForm.phone" label="Telefone" type="tel" autocomplete="tel" hint="Opcional" :disabled="mutation.isPending.value" />
        </template>
      </section>

      <section class="group">
        <h2>Veículo</h2>
        <CkField v-model="vehicleForm.brand" label="Marca" required :error="errors.brand" :disabled="mutation.isPending.value" />
        <CkField v-model="vehicleForm.model" label="Modelo" required :error="errors.model" :disabled="mutation.isPending.value" />
        <CkField v-model="vehicleForm.year" label="Ano" type="number" inputmode="numeric" :maxlength="4" required :error="errors.year" :disabled="mutation.isPending.value" />
        <CkField v-model="vehicleForm.plate" label="Placa" required :error="errors.plate" :disabled="mutation.isPending.value" />
        <CkField v-model="vehicleForm.vin" label="VIN / chassi" hint="Opcional" :disabled="mutation.isPending.value" />
        <CkField v-model="vehicleForm.color" label="Cor" hint="Opcional" :disabled="mutation.isPending.value" />
      </section>

      <section class="group">
        <h2>Ordem de serviço</h2>
        <CkField
          :model-value="expectedDelivery"
          label="Previsão de entrega"
          type="text"
          inputmode="numeric"
          :maxlength="DATE_INPUT_MAX_LENGTH"
          placeholder="dd/MM/yyyy"
          hint="Opcional"
          :error="errors.expectedDelivery"
          :disabled="mutation.isPending.value"
          @update:model-value="updateExpectedDelivery"
        />
      </section>

      <CkButton type="submit" class="submit" :disabled="mutation.isPending.value">{{ mutation.isPending.value ? 'Criando…' : 'Criar ordem de serviço' }}</CkButton>
    </form>
    <BottomNav/>
  </div>
</template>

<style scoped>
.app{max-width:620px;min-height:100vh;margin:auto;padding:calc(20px + env(safe-area-inset-top)) 18px calc(94px + env(safe-area-inset-bottom));background:var(--ck-cream-100)}
header{margin-bottom:20px}
header h1{margin:0;font-size:30px}
.card{padding:18px;margin-bottom:14px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-lg);background:var(--ck-cream-50);box-shadow:var(--shadow-sm)}
.group{margin-bottom:20px}
.group h2{margin:0 0 12px;font-size:14px;text-transform:uppercase;letter-spacing:var(--ls-widest);color:var(--ck-maroon-700)}
.customer-search{margin-bottom:14px;display:flex;flex-direction:column;gap:8px}
.search-results{display:grid;gap:4px;margin:4px 0 0;padding:0;list-style:none}
.search-results button{display:flex;flex-direction:column;align-items:flex-start;gap:2px;width:100%;padding:10px 14px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-md);background:white;text-align:left;cursor:pointer;font:inherit}
.search-results button:hover,.search-results button:focus-visible{border-color:var(--ck-orange-500)}
.search-results span{color:var(--ck-text-muted);font-size:13px}
.empty-inline{margin:4px 0 0;color:var(--ck-text-muted);font-size:13px}
.selected-customer{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;padding:12px 14px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-md);background:white}
.selected-customer span{display:block;color:var(--ck-text-muted);font-size:13px}
.temp-password{display:flex;align-items:center;gap:10px;margin:10px 0;padding:10px 14px;border-radius:var(--radius-md);background:var(--ck-cream-200)}
.temp-password code{font-size:15px;letter-spacing:.04em}
.copy{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--ck-cream-300);border-radius:var(--radius-sm);padding:4px 10px;background:transparent;color:inherit;cursor:pointer;font-size:12px}
.copy:hover{border-color:var(--ck-orange-500);color:var(--ck-orange-500)}
.continue{margin-top:12px}
.submit{width:100%}
</style>
